import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Sparkles, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function ToneSelector({ text, context = 'general', onSelect, className = '' }) {
  const { axiosAuth } = useAuth();
  const [versions, setVersions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const api = axiosAuth();

  const handleGenerate = async () => {
    if (!text?.trim()) return toast.error('No text to transform');
    setGenerating(true);
    setVersions([]);
    setSelected(null);
    try {
      const res = await api.post('/ai/transform-text', { text: text.trim(), context });
      setVersions(res.data.versions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelect = (idx) => {
    setSelected(idx);
    if (onSelect) onSelect(versions[idx]);
    toast.success('Version selected!');
  };

  return (
    <div className={`space-y-3 ${className}`} data-testid="tone-selector">
      <div className="flex items-end gap-3">
        <div className="flex-1 space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">AI Refinement</Label>
          <div className="rounded-full border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-700" data-testid="tone-fixed-label">
            Professional tone for criminal justice documentation
          </div>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={generating || !text?.trim()}
          size="sm"
          className="rounded-full gap-1.5 bg-teal-600 hover:bg-teal-700 text-white shrink-0"
          data-testid="generate-versions-button"
        >
          {generating ? <><Loader2 size={14} className="animate-spin" /> Generating...</> : <><Sparkles size={14} /> Generate 2 Versions</>}
        </Button>
      </div>

      {versions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Choose a version:</p>
          {versions.map((v, i) => (
            <button
              key={`version-${i}`}
              onClick={() => handleSelect(i)}
              className={`w-full text-left p-3 rounded-xl border transition-all text-sm leading-relaxed ${
                selected === i
                  ? 'border-teal-400 bg-teal-50 ring-1 ring-teal-200'
                  : 'border-border bg-muted/20 hover:bg-muted/40'
              }`}
              data-testid={`version-${i + 1}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="text-xs font-semibold text-muted-foreground">Version {i + 1}</span>
                {selected === i && <Check size={14} className="text-teal-600 shrink-0" />}
              </div>
              <p>{v}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
